import React, { useState } from "react";
import { FaIdBadge } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import { useAlert } from "../../../../context/AlertContext";
import {
  useGetEmployeeTemplatesQuery,
  useCreateEmployeeTemplateMutation,
  useUpdateEmployeeTemplateMutation,
  useDeleteEmployeeTemplateMutation,
} from "../../../../store";
import { Drawer, Modal } from "../../../shared";
import TemplateRail from "../TemplateRail";
import { ALL_TEMPLATES } from "../../../../utils/templateGrouping";
import { TextInput } from "../../../../Components/atoms";
import usePermission from "../../../../Hooks/usePermission";

const PERIOD_LABELS = {
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
};

const PERIOD_OPTIONS = [
  { id: "daily", label: "Günlük" },
  { id: "weekly", label: "Haftalık" },
  { id: "monthly", label: "Aylık" },
];

// ─── Template Form Drawer ────────────────────────────────
function TemplateFormDrawer({ open, onClose, firmId, editTemplate, token }) {
  const { showSuccess, showError } = useAlert();
  const [createTemplate, { isLoading: creating }] = useCreateEmployeeTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateEmployeeTemplateMutation();

  const [name, setName] = useState(editTemplate?.name || "");
  const [periodType, setPeriodType] = useState(editTemplate?.periodType || "monthly");
  // periodQuota para (kuruş) tutulur; admin'e TL göster (/100), kaydederken *100.
  const [periodQuota, setPeriodQuota] = useState(
    (editTemplate?.periodQuota ?? 0) / 100
  );

  React.useEffect(() => {
    setName(editTemplate?.name || "");
    setPeriodType(editTemplate?.periodType || "monthly");
    setPeriodQuota((editTemplate?.periodQuota ?? 0) / 100);
  }, [editTemplate]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError("Şablon adı zorunludur!");
      return;
    }
    if (periodQuota < 0) {
      showError("Periyot kotası 0 veya daha büyük olmalıdır!");
      return;
    }

    const payload = {
      token,
      firmId,
      name: name.trim(),
      // Alt rol ayrı girilmez — şablon adıyla aynıdır.
      subRole: name.trim(),
      periodType,
      periodQuota: Math.round((Number(periodQuota) || 0) * 100),
    };

    try {
      if (editTemplate) {
        await updateTemplate({ ...payload, templateId: editTemplate._id }).unwrap();
        showSuccess("Çalışan şablonu güncellendi.");
      } else {
        await createTemplate(payload).unwrap();
        showSuccess("Çalışan şablonu oluşturuldu.");
      }
      onClose();
    } catch (err) {
      showError(err?.data?.message || "İşlem başarısız oldu.");
    }
  };

  const isSubmitting = creating || updating;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editTemplate ? "Şablon Düzenle" : "Yeni Çalışan Şablonu"}
      subtitle="Çalışan şablonu ayarları"
      titleIcon={<FaIdBadge className="w-5 h-5 text-orange-500" />}
      width="md"
    >
      <Drawer.Body className="p-5 space-y-5">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Şablon Adı
          </label>
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="örn: Standart Çalışan, VIP Çalışan"
            disabled={!!editTemplate}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Periyot Tipi
          </label>
          <select
            value={periodType}
            onChange={(e) => setPeriodType(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.id} value={opt.id}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Periyot Kotası
          </label>
          <input
            type="number"
            min={0}
            value={periodQuota}
            onChange={(e) => setPeriodQuota(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-300 focus:border-orange-400 outline-none"
            placeholder="0"
          />
        </div>

      </Drawer.Body>
      <Drawer.Footer className="p-4 border-t border-gray-200">
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            İptal
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="px-4 py-2 text-sm text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50"
          >
            {isSubmitting ? "Kaydediliyor..." : editTemplate ? "Güncelle" : "Oluştur"}
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}

// ─── Ana Bileşen ─────────────────────────────────────────
/**
 * Çalışan şablonları — Çalışanlar sekmesinin solundaki şablon sütunu (#298;
 * eskiden ayrı sekmede kart ızgarasıydı). Seçim ve kişi sayıları çağırandan
 * (EmployeesWithTemplatesTab) gelir; çekmece + silme onayı burada.
 */
export default function EmployeeTemplatesTab({ firm, readOnly, selectedId = ALL_TEMPLATES, onSelect, stats }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();

  const { data, isLoading } = useGetEmployeeTemplatesQuery(
    { token: auth.accessToken, id: firm?._id },
    { skip: !firm?._id }
  );

  const [deleteTemplate, { isLoading: deleting }] = useDeleteEmployeeTemplateMutation();

  const templates = data?.data?.templates || [];

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canCreate = !readOnly && hasPermission("firms.employeeTemplatesCreate");
  const canEdit = !readOnly && hasPermission("firms.employeeTemplatesEdit");
  const canDelete = !readOnly && hasPermission("firms.employeeTemplatesDelete");

  const handleEdit = (template) => {
    setEditTemplate(template);
    setDrawerOpen(true);
  };

  const handleCreate = () => {
    setEditTemplate(null);
    setDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate({
        token: auth.accessToken,
        firmId: firm._id,
        templateId: deleteTarget._id,
      }).unwrap();
      showSuccess("Çalışan şablonu silindi.");
      if (String(selectedId) === String(deleteTarget._id)) onSelect?.(ALL_TEMPLATES);
      setDeleteTarget(null);
    } catch (err) {
      showError(err?.data?.message || "Silme işlemi başarısız oldu.");
    }
  };

  // Seçili şablonun özeti: dönem + dönem kotası (kuruş → TL).
  const renderSummary = (t) => (
    <div className="flex flex-wrap gap-1.5">
      <span className="px-2 py-0.5 text-[11px] bg-blue-50 text-blue-700 rounded-md border border-blue-200 font-medium">
        {PERIOD_LABELS[t.periodType] || t.periodType}
      </span>
      <span className="px-2 py-0.5 text-[11px] bg-emerald-50 text-emerald-700 rounded-md border border-emerald-200 font-medium">
        Kota: ₺{((Number(t.periodQuota) || 0) / 100).toFixed(2)}
      </span>
    </div>
  );

  return (
    <>
      <TemplateRail
        title="Çalışan Şablonları"
        accent="orange"
        templates={templates}
        stats={stats}
        selectedId={selectedId}
        onSelect={onSelect}
        loading={isLoading}
        canCreate={canCreate}
        onCreate={handleCreate}
        canEdit={canEdit}
        onEdit={handleEdit}
        canDeleteTemplate={() => canDelete}
        onDelete={setDeleteTarget}
        renderSummary={renderSummary}
      />

      {/* Form Drawer — her zaman renderlanmalı */}
      <TemplateFormDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditTemplate(null);
        }}
        firmId={firm?._id}
        editTemplate={editTemplate}
        token={auth.accessToken}
      />

      {/* Silme Onay Modal */}
      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <Modal.Header title="Şablonu Sil" onClose={() => setDeleteTarget(null)} />
        <Modal.Body>
          <p className="text-sm text-gray-600">
            <strong>{deleteTarget?.name}</strong> çalışan şablonunu silmek istediğinize emin misiniz?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteTarget(null)}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              İptal
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 text-sm text-white bg-fifth rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              {deleting ? "Siliniyor..." : "Sil"}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
